import React from 'react';

const FormVariableInput = ({
    selectedTemplate,
    variables,
    onVariableChange
}) => {
    if (!selectedTemplate) return null;

    // 텍스트 내 변수 치환 헬퍼 함수
    const replaceVariables = (text) => {
        if (!text) return '';
        let result = text;

        // 1. 현재 입력된 변수들로 치환
        Object.keys(variables).forEach(key => {
            // {{변수명}} 패턴 치환
            const regex = new RegExp(`{{${key}}}`, 'g');
            result = result.replace(regex, variables[key] || `[${key}]`);
        });

        // 2. 입력되지 않은 나머지 변수들은 [변수명] 형태로 표시
        // (정규식을 사용해 {{...}} 패턴을 찾아서 처리할 수도 있지만, 
        //  여기서는 이미 입력된 variables에 대해서만 처리하고 나머지는 그대로 두거나 
        //  템플릿에 정의된 variables 목록을 순회하며 처리하는 것이 안전함)
        if (selectedTemplate.variables) {
            selectedTemplate.variables.forEach(key => {
                if (!variables[key]) {
                    const regex = new RegExp(`{{${key}}}`, 'g');
                    result = result.replace(regex, `[${key}]`);
                }
            });
        }

        return result;
    };

    const getQuestionTypeLabel = (type) => {
        const typeMap = {
            'SHORT_ANSWER': '단답형',
            'PARAGRAPH': '장문형',
            'MULTIPLE_CHOICE': '객관식',
            'CHECKBOXES': '체크박스',
            'DROPDOWN': '드롭다운'
        };
        return typeMap[type] || type;
    };

    // 질문 유형 텍스트 색상 스타일 (뱃지 대신 텍스트 색상 적용)
    const getTextColorStyle = (type) => {
        switch (type) {
            case 'SHORT_ANSWER': return { color: '#0d6efd' }; // 단답형 - 파랑 (Bootstrap Primary)
            case 'PARAGRAPH': return { color: '#198754' }; // 장문형 - 초록 (Bootstrap Success)
            case 'MULTIPLE_CHOICE': return { color: '#FFD700' }; // 객관식 - 노랑(Gold)
            case 'CHECKBOXES': return { color: '#000080' }; // 체크박스 - 남색(Navy)
            case 'DROPDOWN': return { color: '#6c757d' }; // 드롭다운 - 회색 (Bootstrap Secondary)
            default: return { color: '#6c757d' };
        }
    };

    return (
        <div>
            <h5 className="mb-3">템플릿 변수 입력</h5>
            <div className="alert alert-info bg-white mb-3" style={{ borderColor: 'currentColor' }}>
                <strong>폼 제목 미리보기:</strong> {replaceVariables(selectedTemplate.titleTemplate)}
            </div>

            {/* 변수 입력 */}
            {selectedTemplate.variables && selectedTemplate.variables.map((variable, index) => (
                <div className="mb-3" key={index}>
                    <label className="form-label">{variable}</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={`${variable} 입력`}
                        value={variables[variable] || ''}
                        onChange={(e) => onVariableChange(variable, e.target.value)}
                        required
                    />
                </div>
            ))}

            {/* 폼 설명 미리보기 (설명이 있을 경우에만 표시) */}
            {selectedTemplate.description && (
                <div className="mb-4">
                    <h5 className="mb-3">폼 설명 미리보기</h5>
                    <div className="alert alert-secondary bg-light border-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {replaceVariables(selectedTemplate.description)}
                    </div>
                </div>
            )}

            <hr className="my-4" />

            {/* 질문 미리보기 */}
            <h5 className="mb-3">이 템플릿에 포함된 질문들</h5>
            {selectedTemplate.questions && selectedTemplate.questions.length > 0 ? (
                <div className="mb-4">
                    {selectedTemplate.questions.map((question, index) => (
                        <div key={index} className="card mb-3 border border-light shadow-sm rounded-3">
                            <div className="card-body p-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="card-title fw-bold text-dark mb-0 fs-5">
                                        <span className="text-primary me-2">Q{index + 1}.</span>
                                        {replaceVariables(question.title)}
                                        {question.required && <span className="text-danger ms-1" title="필수 질문">*</span>}
                                    </h6>
                                    <span style={{ fontWeight: 600, ...getTextColorStyle(question.type) }}>
                                        {getQuestionTypeLabel(question.type)}
                                    </span>
                                </div>

                                {question.description && (
                                    <div className="mb-2 text-muted" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                                        {replaceVariables(question.description)}
                                    </div>
                                )}

                                <div className="ps-4">
                                    {['MULTIPLE_CHOICE', 'CHECKBOXES', 'DROPDOWN'].includes(question.type) && (
                                        <ul className="list-group list-group-flush bg-transparent">
                                            {question.options && question.options.map((opt, oIndex) => (
                                                <li key={oIndex} className="list-group-item bg-transparent py-2 border-0 ps-0 d-flex align-items-center">
                                                    <span className="material-symbols-rounded fs-6 text-muted me-2">radio_button_unchecked</span>
                                                    <span className="text-dark"><small>{replaceVariables(opt)}</small></span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {['SHORT_ANSWER'].includes(question.type) && (
                                        <div className="text-muted small p-3 border rounded bg-light">
                                            단답형 텍스트 입력 영역
                                        </div>
                                    )}
                                    {['PARAGRAPH'].includes(question.type) && (
                                        <div className="text-muted small p-3 border rounded bg-light" style={{ minHeight: '80px' }}>
                                            장문형 텍스트 입력 영역
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="alert alert-warning">
                    질문 정보가 없습니다.
                </div>
            )}
        </div>
    );
};

export default FormVariableInput;
